import React, { useState, useEffect } from "react";
import ReactTable from "react-table";
import { useTranslation } from "react-i18next";
import range from "lodash/range";
import { translations } from "helpers/react-table";
import { useSelector } from "react-redux";
import { getAllElecPees, getAllGazPees } from "reducers/currentSelection";
import * as XLSX from "xlsx";

const getPageSizeOptions = (total) => {
  if (!total || total === 0) return [10, 15, 20];
  const baseOptions = [10, 15].concat(
    range(7)
      .map((i) => i * 5 + 20)
      .filter((n) => n < total),
  );
  return Array.from(new Set([...baseOptions, total]));
};

export const EtudeSiteTable = ({
  energyType,
  selectedIds,
  onChangeSelection,
}) => {
  if (!energyType) {
    return (
      <div className="text-muted p-3">
        Veuillez d'abord sélectionner une énergie.
      </div>
    );
  }

  const { t } = useTranslation();
  const [selectedRows, setSelectedRows] = useState({});
  const [uploadedClefs, setUploadedClefs] = useState(null);
  const fileInputRef = React.useRef(null);

  const elecPees = useSelector(getAllElecPees);
  const gasPees = useSelector(getAllGazPees);

  let pees = [];
  if (energyType === 1) {
    pees = gasPees;
  } else if (energyType === 2) {
    pees = elecPees;
  }

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      const bstr = evt.target.result;
      const wb = XLSX.read(bstr, { type: "binary" });

      const wsname = wb.SheetNames[0];
      const ws = wb.Sheets[wsname];

      const excelData = XLSX.utils.sheet_to_json(ws, { header: 1 });

      const clefs = excelData
        .flat()
        .filter((val) => val !== null && val !== undefined)
        .map((val) => String(val).trim())
        .filter((val) => val !== "");

      setUploadedClefs(clefs);
    };

    reader.readAsBinaryString(file);
  };

  const clearFileFilter = () => {
    setUploadedClefs(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  useEffect(() => {
    const selectedArray = Object.keys(selectedRows)
      .filter((key) => selectedRows[key])
      .map((id) => Number(id));

    if (onChangeSelection) {
      onChangeSelection(selectedArray);
    }
  }, [selectedRows, onChangeSelection]);

  const data = React.useMemo(() => {
    if (!pees || pees.length === 0) return [];

    let filteredPees = Object.values(pees);

    if (uploadedClefs !== null) {
      filteredPees = filteredPees.filter((pee) =>
        uploadedClefs.includes(pee.PeeClef),
      );
    }

    return filteredPees.map((pee) => ({
      id: pee.id || pee.Id || pee.PeeClef,
      clef: pee.PeeClef,
      country: pee.LibellePays,
      energy: pee.PeeNrj,
    }));
  }, [pees, uploadedClefs]);

  const toggleRow = (id) => {
    setSelectedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const toggleAll = () => {
    const isAllSelected =
      data.length > 0 &&
      Object.keys(selectedRows).filter((k) => selectedRows[k]).length ===
        data.length;

    const newSelectedRows = {};
    if (!isAllSelected) {
      data.forEach((row) => {
        newSelectedRows[row.id] = true;
      });
    }
    setSelectedRows(newSelectedRows);
  };

  const isAllSelected =
    data.length > 0 &&
    Object.keys(selectedRows).filter((k) => selectedRows[k]).length ===
      data.length;

  const columns = React.useMemo(
    () =>
      [
        {
          id: "checkbox",
          accessor: "",
          Cell: ({ original }) => {
            return (
              <input
                type="checkbox"
                className="checkbox"
                checked={selectedRows[original.id] === true}
                onChange={() => toggleRow(original.id)}
                onClick={(e) => e.stopPropagation()}
              />
            );
          },
          Header: () => {
            return (
              <input
                type="checkbox"
                className="checkbox"
                checked={isAllSelected}
                onChange={toggleAll}
              />
            );
          },
          sortable: false,
          filterable: false,
          width: 45,
        },
        {
          Header: t("etudes.creation.table.meter") || "Clef",
          accessor: "clef",
        },
        {
          Header: t("etudes.creation.table.country") || "Country",
          accessor: "country",
        },
        {
          Header: t("etudes.creation.table.energy") || "Energy",
          accessor: "energy",
        },
      ].map((column) =>
        column.filterable === false
          ? column
          : {
              ...column,
            },
      ),
    [t, data, selectedRows, isAllSelected],
  );

  return (
    <div>
      <div className="d-flex align-items-center mb-3 p-2 bg-light border rounded">
        <div
          className="mr-3 font-weight-bold"
          style={{ fontSize: "13px", color: "#61747f" }}
        >
          {t("etudes.creation.table.filterByFile")} :
        </div>
        <input
          type="file"
          accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
          onChange={handleFileUpload}
          ref={fileInputRef}
          style={{ fontSize: "13px" }}
        />
        {uploadedClefs && (
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary ml-auto"
            onClick={clearFileFilter}
            style={{ fontSize: "12px", padding: "2px 8px" }}
          >
            {t("etudes.creation.table.resetFilter")}
          </button>
        )}
      </div>

      <ReactTable
        data={data}
        columns={columns}
        defaultPageSize={5}
        pageSizeOptions={getPageSizeOptions(data.length)}
        getTrProps={(_, rowInfo) => {
          if (rowInfo && rowInfo.original) {
            const isSelected = selectedRows[rowInfo.original.id];
            return {
              onClick: () => toggleRow(rowInfo.original.id),
              style: { cursor: "pointer" },
              className: isSelected ? "-selected" : "",
            };
          }
          return { style: { cursor: "unset" } };
        }}
        {...translations(t)}
        className="shadow-sm -striped -highlight"
        css={{
          fontSize: 13,
          ".rt-thead.-filters .rt-th": {
            overflow: "unset",
          },
          ".-selected": {
            backgroundColor: "rgba(0, 123, 255, 0.1) !important",
          },
        }}
        filterable
      />
    </div>
  );
};
